/**
 * Generic fallback — used for `header`, `source`, `rail`, `other`, and
 * any future ComponentKind we haven't built a symbol for yet. Renders
 * as a labelled rectangle similar to ICNode but with a faded body to
 * signal "no specific symbol available".
 */
import { useTheme } from "@mui/material";
import type { NodeProps } from "@xyflow/react";

import { SymbolFrame } from "./SymbolFrame";
import { LABEL_STYLE, SYMBOL_STROKE_WIDTH, symbolColors } from "./symbolStyle";

interface GenericData {
  refdes?: string;
  value?: string | null;
  kind?: string;
  highlight?: boolean;
}

export function GenericNode({ data, width, height }: NodeProps) {
  const t = useTheme();
  const { stroke, fill, text, faint } = symbolColors(t);
  const d = data as GenericData;
  const w = (width ?? 100) - 6;
  const h = (height ?? 60) - 12;
  return (
    <SymbolFrame width={width} height={height} highlight={d.highlight}>
      <svg
        viewBox={`0 0 ${w} ${h}`}
        width={w}
        height={h}
        fill={fill}
        stroke={stroke}
        strokeWidth={SYMBOL_STROKE_WIDTH}
        strokeDasharray="3 3"
      >
        <rect x={1} y={1} width={w - 2} height={h - 2} rx={2} ry={2} />
        <text
          x={w / 2}
          y={h / 2 - 4}
          textAnchor="middle"
          fill={text}
          stroke="none"
          {...LABEL_STYLE}
          fontWeight={600}
          fontSize={11}
        >
          {d.refdes ?? d.kind ?? ""}
        </text>
        {d.value && (
          <text
            x={w / 2}
            y={h / 2 + 9}
            textAnchor="middle"
            fill={faint}
            stroke="none"
            {...LABEL_STYLE}
            fontSize={9}
          >
            {d.value.length > 16 ? `${d.value.slice(0, 14)}…` : d.value}
          </text>
        )}
      </svg>
    </SymbolFrame>
  );
}
